import { useGT } from 'gt-react';
import { DEFAULT_TASK_OUTCOMES, TASK_STATUSES } from '@erp/shared-types';
import type { TaskStatus } from '@erp/shared-types';
import type { MultiSelectOption } from 'src/components/common/MultiSelect';
import type { SingleSelectOption } from 'src/components/common/SingleSelect';
import type { TasksQueueSearch } from './taskQueueSearch';

type Translate = ReturnType<typeof useGT>;

export function toTaskStatuses(ids: readonly string[]): TaskStatus[] {
  return TASK_STATUSES.filter((status) => ids.includes(status));
}

export function selectedOption(
  options: SingleSelectOption[],
  id: string | undefined,
): SingleSelectOption | null {
  if (!id) return null;
  return options.find((option) => option.id === id) ?? { id, label: id };
}

export function buildTypeOptions(
  taskTypes: Array<{ key: string; name: string }> | undefined,
): SingleSelectOption[] {
  return (taskTypes ?? []).map((t) => ({
    id: t.key,
    label: t.name,
  }));
}

const STATUS_FILTER_RANK: Record<TaskStatus, number> = {
  pending: 0,
  in_progress: 1,
  needs_input: 2,
  completed: 3,
  failed: 4,
  cancelled: 5,
};

export function buildStatusOptions(gt: Translate): MultiSelectOption[] {
  const labels: Record<TaskStatus, string> = {
    pending: gt('Pending', { context: 'task status' }),
    in_progress: gt('In progress', { context: 'task status' }),
    needs_input: gt('Needs input', { context: 'task status' }),
    completed: gt('Done', { context: 'task status' }),
    failed: gt('Failed', { context: 'task status' }),
    cancelled: gt('Cancelled', { context: 'task status' }),
  };
  return [...TASK_STATUSES]
    .sort((left, right) => STATUS_FILTER_RANK[left] - STATUS_FILTER_RANK[right])
    .map((id) => ({ id, label: labels[id] }));
}

export type AssigneeActor = { id: string; name: string };

export function buildAssigneeFacetInput({
  taskTypeId,
  statuses,
  outcome,
  excludeSnoozed,
}: {
  taskTypeId?: string;
  statuses?: TaskStatus[];
  outcome?: string;
  /** Match the queue: facet counts omit actively snoozed tasks when the list does. */
  excludeSnoozed?: boolean;
}): {
  taskTypeId?: string;
  statuses?: TaskStatus[];
  outcome?: string;
  excludeSnoozed?: boolean;
} {
  return {
    ...(taskTypeId ? { taskTypeId } : {}),
    ...(statuses && statuses.length > 0 ? { statuses } : {}),
    ...(outcome ? { outcome } : {}),
    ...(excludeSnoozed ? { excludeSnoozed: true } : {}),
  };
}

/** Keep the URL-selected assignee in the picker if they are missing from facets. */
export function withSelectedActor({
  actors,
  selectedAssignee,
  kind,
  lookup,
}: {
  actors: AssigneeActor[];
  selectedAssignee: string | undefined;
  kind: 'user' | 'agent';
  lookup: AssigneeActor[] | undefined;
}): AssigneeActor[] {
  const prefix = `${kind}:`;
  if (!selectedAssignee?.startsWith(prefix)) return actors;
  const id = selectedAssignee.slice(prefix.length);
  if (actors.some((actor) => actor.id === id)) return actors;
  const found = lookup?.find((actor) => actor.id === id);
  if (!found) return actors;
  return [...actors, found];
}

/** Facet assignees, or the org lists if the facet query failed. */
export function assigneePickerActors({
  facets,
  useFallback,
  fallbackUsers,
  fallbackAgents,
  selectedAssignee,
}: {
  facets: { users: AssigneeActor[]; agents: AssigneeActor[] } | undefined;
  useFallback: boolean;
  fallbackUsers: AssigneeActor[] | undefined;
  fallbackAgents: AssigneeActor[] | undefined;
  selectedAssignee: string | undefined;
}): { users: AssigneeActor[]; agents: AssigneeActor[] } {
  const users = useFallback ? (fallbackUsers ?? []) : (facets?.users ?? []);
  const agents = useFallback ? (fallbackAgents ?? []) : (facets?.agents ?? []);
  return {
    users: withSelectedActor({
      actors: users,
      selectedAssignee,
      kind: 'user',
      lookup: fallbackUsers,
    }),
    agents: withSelectedActor({
      actors: agents,
      selectedAssignee,
      kind: 'agent',
      lookup: fallbackAgents,
    }),
  };
}

export function buildAssigneeOptions({
  gt,
  unassignedLabel,
  users,
  agents,
}: {
  gt: Translate;
  /** Translated in the toolbar so the string extractor sees the call. */
  unassignedLabel: string;
  users: Array<{ id: string; name: string }> | undefined;
  agents: Array<{ id: string; name: string }> | undefined;
}): SingleSelectOption[] {
  return [
    {
      id: 'mine',
      label: gt('Me', { context: 'assignee filter, tasks assigned to me' }),
    },
    {
      id: 'unassigned',
      label: unassignedLabel,
    },
    ...(users ?? []).map((u) => ({
      id: `user:${u.id}`,
      label: u.name,
      group: gt('Users', { context: 'assignee filter group' }),
    })),
    ...(agents ?? []).map((a) => ({
      id: `agent:${a.id}`,
      label: a.name,
      group: gt('Agents', { context: 'assignee filter group' }),
    })),
  ];
}

/**
 * Outcomes the queue's filtered type can close with: its outcome Core Enum's
 * options, else the defaults. Empty while the selected type (or its enum
 * options) are still loading, so the defaults never flash for a custom type.
 */
export function queueOutcomeVocabulary(
  selection:
    | { kind: 'none' }
    | { kind: 'type'; type: { outcomeEnumId: string | null } | undefined },
  loaded: readonly string[] | undefined,
): readonly string[] {
  if (selection.kind === 'none') return DEFAULT_TASK_OUTCOMES;
  if (!selection.type) return [];
  const { outcomeEnumId } = selection.type;
  if (!outcomeEnumId) return DEFAULT_TASK_OUTCOMES;
  if (loaded === undefined) return [];
  return loaded.length > 0 ? loaded : DEFAULT_TASK_OUTCOMES;
}

export function buildOutcomeOptions(
  outcome: string | undefined,
  vocabulary: readonly string[] = DEFAULT_TASK_OUTCOMES,
): SingleSelectOption[] {
  const extras =
    outcome && !vocabulary.includes(outcome)
      ? [{ id: outcome, label: outcome }]
      : [];
  return [
    ...vocabulary.map((value) => ({ id: value, label: value })),
    ...extras,
  ];
}

export function isQueueFiltered(searchParams: TasksQueueSearch): boolean {
  return Boolean(
    searchParams.type ||
    searchParams.status?.length ||
    searchParams.assignee ||
    searchParams.outcome ||
    searchParams.search ||
    Object.keys(searchParams.ref ?? {}).length,
  );
}
